import React, { useState } from 'react';
import { Spacer } from 'src/common';
import { Guard } from '..';
import WorkspaceVariablesForm from 'src/components/formComposed/WorkspaceVariablesForm';
import { Scope } from 'src/components/formComposed/WorkspaceVariablesForm/types';
import { KeyType } from 'src/graphql';
import styled from 'styled-components';
import { Modal } from '../comps';
import { Head } from './comps/Head';
import TabsMenu from 'src/components/TabsMenu';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { FromCSV } from './forms/FromCSV';
import { CsvImportPreview } from 'src/components/VariableCSVReader/types';

export const StyledModal = styled(Modal)`
  width: 1100px;
  min-height: 200px;
  padding: 0 !important;
  overflow-y: auto;
  max-height: 100%;
  overflow-x: auto;
`;

interface IVariablesWorkspaceModalProps {
  onClose: () => void;
  scope: Scope;
  readOnly?: boolean;
  initHideEmptyVariables?: boolean;
  invalidateQueries?: KeyType[];
  /** When set, the form scrolls to / focuses / pulse-highlights this variable's row. */
  focusVariableId?: number | null;
}

export const VariablesWorkspaceModal: React.FC<IVariablesWorkspaceModalProps> = ({
  onClose,
  scope,
  readOnly,
  initHideEmptyVariables = true,
  invalidateQueries,
  focusVariableId,
}) => {
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showAssessmentVarOnly, setShowAssessmentVarOnly] = useState(false);
  const [hideEmptyVariables, setHideEmptyVariables] = useState(initHideEmptyVariables);
  const [activeTab, setActiveTab] = useState<'variables' | 'from_csv'>('variables');
  const [pendingCsvImport, setPendingCsvImport] = useState<CsvImportPreview | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFormDirty = isDirty || !!pendingCsvImport;

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={isFormDirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={false} persistent={isSubmitting}>
          <Spacer $px={40} />
          <Head
            scope={scope}
            setShowAssessmentVarOnly={setShowAssessmentVarOnly}
            setHideEmptyVariables={setHideEmptyVariables}
            showAssessmentVarOnly={showAssessmentVarOnly}
            hideEmptyVariables={hideEmptyVariables}
            showHideEmptyVariables={activeTab === 'variables'}
          />
          <Spacer $px={40} />
          <div className="h-padding">
            <TabsMenu
              tabs={[
                { label: 'Variables', value: 'variables', dataCy: 'variables' },
                { label: 'Import from CSV', value: 'from_csv', dataCy: 'from_csv' },
              ]}
              activeTab={activeTab}
              setActiveTab={(tab) => {
                setActiveTab(tab);
                setPendingCsvImport(null);
                mpEvent(MPEvents.ButtonClick, {
                  button: tab === 'variables' ? 'Tab:Variables' : 'Tab:Import from CSV',
                  modal: 'Variables modal',
                  tags: ['VARIABLES'],
                });
              }}
            />
          </div>
          <Spacer $px={45} />
          {activeTab === 'variables' && (
            <WorkspaceVariablesForm
              onClose={onClose}
              onCloseRequest={() => setIsCloseRequested(true)}
              scope={scope}
              onDirtyChange={setIsDirty}
              onSubmittingChange={setIsSubmitting}
              invalidateQueries={invalidateQueries}
              readOnly={readOnly}
              showAssessmentVarOnly={showAssessmentVarOnly}
              hideEmptyVariables={hideEmptyVariables}
              focusVariableId={focusVariableId}
              isModal
            />
          )}
          {activeTab === 'from_csv' && (
            <FromCSV
              scope={scope}
              pendingImport={pendingCsvImport}
              onPendingImportChange={setPendingCsvImport}
              onClose={onClose}
            />
          )}
          <Spacer $px={54} />
        </StyledModal>
      }
    />
  );
};
